import { StandardAsset } from '$components/Asset';
import { getAssetProps, getSourceFromFields } from '$providers/Assets';
import { CtaProvider } from '$providers/CtaProvider';
import cn from 'classnames';
import { HTMLAttributes, useEffect, useState } from 'react';

import { ContentBlock, IContentBlock } from '../ContentBlock/index';
import FadeInSection from '../FadeInSection/FadeInSection';
import { CardContentLayout, CardSize, ContentAspect, ICard } from './';

const sizeClasses: Record<CardSize, string> = {
  medium: 'px-6 py-6 lg:px-16 lg:py-20',
  small: 'px-6 py-6 lg:p-8',
};

const aspectRatios: Record<ContentAspect, string> = {
  '1:1': 'md:aspect-[1/1] aspect-[1/1]',
  '3:1': 'md:aspect-[3/1] aspect-[1/3]',
  '16:9': 'md:aspect-[16/9] aspect-[2/3]',
  'content-size': '',
};

const layoutClasses: Record<CardContentLayout, string> = {
  left: 'items-left',
  center: 'items-center',
};

export function Card(props: ICard & HTMLAttributes<HTMLElement>) {
  const {
    margins = true,
    cardLayout,
    size,
    assets,
    cardIndex,
    ctaCollection,
    aspect,
    layout,
    widthLimit = true,
  } = props;

  const cardContent = {
    heading: props.heading,
    subHeading: props.subHeading,
    content: props.content,
    layout: props.layout,
    type: props.type,
    size: props.contentSize,
    richText: props.richText,
    button: ctaCollection,
    widthLimit,
  } as IContentBlock;

  const alignment = layoutClasses[layout] ?? layoutClasses.left;

  const marginsDefault = margins ?? true;
  const paddingClasses = marginsDefault ? sizeClasses[size] : '';

  const [devicePixelRatio, setDevicePixelRatio] = useState(1);
  useEffect(() => {
    setDevicePixelRatio(Math.min(2, window.devicePixelRatio));
  }, []);

  let cardElement = null;
  switch (cardLayout) {
    case 'image-behind': {
      cardElement = (
        <div
          className={cn(
            paddingClasses,
            aspectRatios[aspect],
            'w-full flex items-center bg-no-repeat bg-center bg-cover',
            {
              'justify-center': props.layout === 'center',
              'mt-6 lg:mt-0 py-32': marginsDefault,
            },
          )}
        >
          <StandardAsset
            assets={assets}
            sizes={
              '(max-width: ' +
              Math.floor(768 * devicePixelRatio) +
              'px) ' +
              Math.floor(768 / devicePixelRatio) +
              'px, ' +
              Math.floor(1440 / devicePixelRatio) +
              'px'
            }
            className={cn(
              'absolute top-0 z-0 left-0 rounded-[2.5rem] overflow-hidden',
            )}
          />
          <ContentBlock {...cardContent} />
        </div>
      );
      break;
    }
    case 'image-left': {
      cardElement = (
        <>
          <div className={cn('lg:w-1/2 w-full', aspectRatios[aspect])}>
            <StandardAsset
              assets={assets}
              sizes="(min-width: 1024px) 50vw, 100vw"
              className={cn(
                'lg:h-auto object-cover object-center rounded-[2.5rem] overflow-hidden mb-10 lg:mb-0',
              )}
            />
          </div>
          <div
            className={cn(
              'lg:w-1/2 w-full px-4 lg:pl-16 lg:py-4 xl:pl-32 xl:py-6 flex items-center',
            )}
          >
            <ContentBlock {...cardContent} />
          </div>
        </>
      );
      break;
    }
    case 'image-right': {
      cardElement = (
        <>
          <div
            className={cn(
              'lg:w-1/2 w-full px-4 lg:pr-16 lg:py-4 xl:pr-32 xl:py-6 flex items-center',
            )}
          >
            <ContentBlock {...cardContent} />
          </div>
          <div
            className={cn(
              'lg:w-1/2 w-full mt-10 lg:mt-0',
              aspectRatios[aspect],
            )}
          >
            <StandardAsset
              assets={assets}
              sizes="(min-width: 1024px) 50vw, 100vw"
              className={cn(
                'lg:h-auto object-cover object-center rounded-[2.5rem] overflow-hidden',
              )}
            />
          </div>
        </>
      );
      break;
    }
    case 'text-bottom': {
      cardElement = (
        <>
          <div className={cn(aspectRatios[aspect], 'flex w-full')}>
            <div
              className={cn(
                paddingClasses,
                'dark w-full flex justify-end bg-no-repeat bg-center bg-cover flex flex-wrap flex-col rounded-[2.5rem] overflow-hidden relative',
                alignment,
              )}
            >
              <StandardAsset
                assets={assets}
                sizes={
                  '(max-width: ' +
                  Math.floor(768 * devicePixelRatio) +
                  'px) ' +
                  Math.floor(768 / devicePixelRatio) +
                  'px, ' +
                  Math.floor(1440 / devicePixelRatio) +
                  'px'
                }
                className={cn('absolute top-0 z-0 left-0')}
              />
              <ContentBlock {...cardContent} />
            </div>
          </div>
        </>
      );
      break;
    }
    case 'text-top': {
      cardElement = (
        <>
          <div className={cn(aspectRatios[aspect], 'flex w-full')}>
            <div
              className={cn(
                paddingClasses,
                'dark w-full flex justify-start bg-no-repeat bg-center bg-cover flex-wrap flex-col rounded-[2.5rem] overflow-hidden relative',
                alignment,
              )}
            >
              <StandardAsset
                assets={assets}
                sizes={
                  '(max-width: ' +
                  Math.floor(768 * devicePixelRatio) +
                  'px) ' +
                  Math.floor(768 / devicePixelRatio) +
                  'px, ' +
                  Math.floor(1440 / devicePixelRatio) +
                  'px'
                }
                className={cn('absolute top-0 z-0 left-0')}
              />
              <ContentBlock {...cardContent} />
            </div>
          </div>
        </>
      );
      break;
    }
    case 'banners': {
      cardElement = (
        <>
          <div className={cn(aspectRatios[aspect], 'flex w-full')}>
            <div
              className={cn(
                paddingClasses,
                'dark w-full flex justify-start bg-no-repeat bg-center bg-cover flex-wrap flex-col overflow-hidden relative',
                alignment,
              )}
            >
              <ContentBlock {...cardContent} button={null} />
              <div className="w-full flex flex-row flex-wrap gap-x-11	lg:gap-x-28 gap-y-8 justify-center">
                {assets.map((asset, index) => {
                  const assetSource = getSourceFromFields({ asset });
                  if (
                    assetSource.assetType === 'image' &&
                    assetSource.assetOrigin === 'contentful'
                  ) {
                    return (
                      <div
                        className="w-[64px] h-[32px] lg:w-[96px] lg:h-[48px]"
                        key={index}
                      >
                        <img
                          width={getAssetProps(assetSource.image).width}
                          height={getAssetProps(assetSource.image).height}
                          alt={
                            getAssetProps(assetSource.image).description || ''
                          }
                          src={getAssetProps(assetSource.image).url}
                          className="w-full h-full object-fill"
                          loading="lazy"
                        />
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
              {ctaCollection && (
                <div
                  className={cn('mt-4 lg:mt-8 flex flex-row gap-4', {
                    'text-center items-center justify-center':
                      layout === 'center',
                  })}
                >
                  {ctaCollection.map((button, index) => {
                    return (
                      <CtaProvider
                        buttonType={props.type}
                        key={index}
                        size="medium"
                        item={button}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      );
      break;
    }
  }

  return (
    <FadeInSection delay={cardIndex * 200}>
      <div className={cn('mx-auto flex flex-wrap overflow-hidden relative')}>
        {cardElement}
      </div>
    </FadeInSection>
  );
}
